import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router";
import { toast } from "sonner";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { DatePicker } from "@/components/ui/date-picker";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import { useAuthStore } from "@/features/auth/stores/authStore";
import { useDebounce } from "@/shared/hooks/useDebounce";
import { TablePagination } from "@/shared/components/TablePagination";
import {
  type Order,
  type SalesReviewQueueItem,
  ordersApi,
} from "@/features/production/services/ordersApi";
import {
  useConfirmDates,
  useConfirmOrderWithAllocation,
  useOrdersAwaitingSalesReview,
  usePushBackAgronomistOrder,
  usePublishReadyDate,
  useSalesReviewAllocationPreview,
} from "@/features/production/hooks/useOrders";
import { ViewOrderDialog } from "@/features/production/components/orders/ViewOrderDialog";

const formatKes = (value: number) =>
  new Intl.NumberFormat("en-KE", {
    style: "currency",
    currency: "KES",
    minimumFractionDigits: 2,
  }).format(value);

const formatDateOrDash = (value?: string | null) => {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "-";
  if (d.getUTCFullYear() <= 1970) return "-";
  return d.toLocaleDateString();
};

const formatQty = (value: number) =>
  new Intl.NumberFormat("en-KE", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(value);

const formatDateOrTbd = (value?: string | null) => {
  if (!value) return "TBD";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "TBD";
  return d.toLocaleDateString();
};

const stageLabelMap: Record<string, string> = {
  ReadyForDispatch: "Ready For Dispatch",
  Growing: "Growing",
  Germinated: "Germinated",
  Sowing: "Sowing",
};

const toDateInputValue = (value?: string | null) => {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toISOString().slice(0, 10);
};

const getItemSummary = (order: SalesReviewQueueItem) => {
  const labels = order.orderItems
    .map((item) => {
      const name = item.variety
        ? `${item.productName} - ${item.variety}`
        : item.productName;
      const tray = item.trayTypeName ? ` (${item.trayTypeName})` : "";
      const qty = item.quantity ? ` × ${item.quantity}` : "";
      return name ? `${name}${tray}${qty}` : null;
    })
    .filter(Boolean) as string[];

  if (labels.length === 0) return "-";
  if (labels.length <= 2) return labels.join(" | ");
  return `${labels.slice(0, 2).join(" | ")} +${labels.length - 2} more`;
};

const getBalanceAmount = (order: SalesReviewQueueItem) => {
  const total = Number(order.totalAmount ?? 0);
  const paid = Number(order.paidAmount ?? 0);
  const computed = total - paid;
  const balance = Number(order.balanceAmount ?? computed);
  return Math.max(balance, 0);
};

export const SalesReviewQueuePage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { hasPermission } = useAuthStore();
  const canConfirm = hasPermission("orders.confirm_with_allocation");
  const canPublishReadyDate = hasPermission("orders.publish_ready_date");
  const canConfirmDates = hasPermission("orders.confirm_dates");
  const canPushBack = hasPermission("orders.push_back_amendment");

  const confirmMutation = useConfirmOrderWithAllocation();
  const publishMutation = usePublishReadyDate();
  const datesMutation = useConfirmDates();
  const pushBackMutation = usePushBackAgronomistOrder();

  const [search, setSearch] = useState(() => searchParams.get("q") ?? "");
  const debouncedSearch = useDebounce(search, 300);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const queueQuery = useOrdersAwaitingSalesReview({
    search: debouncedSearch.trim() || undefined,
    pageNumber,
    pageSize,
  });
  const paramsString = searchParams.toString();

  useEffect(() => {
    setPageNumber(1);
  }, [debouncedSearch]);

  useEffect(() => {
    const params = new URLSearchParams(paramsString);
    const nextSearch = params.get("q") ?? "";
    setSearch((current) => (current === nextSearch ? current : nextSearch));
  }, [paramsString]);

  useEffect(() => {
    const next = new URLSearchParams();
    if (search) next.set("q", search);

    const nextString = next.toString();
    if (nextString !== paramsString) {
      setSearchParams(next, { replace: true });
    }
  }, [search, paramsString, setSearchParams]);

  const [viewTarget, setViewTarget] = useState<Order | null>(null);
  const [viewLoadingId, setViewLoadingId] = useState<string | null>(null);
  const [reviewTarget, setReviewTarget] = useState<SalesReviewQueueItem | null>(null);
  const [publishTarget, setPublishTarget] = useState<SalesReviewQueueItem | null>(null);

  const allocationPreviewQuery = useSalesReviewAllocationPreview(
    reviewTarget?.id ?? "",
    Boolean(reviewTarget)
  );

  const [decision, setDecision] = useState<1 | 3>(1);
  const [reviewNotes, setReviewNotes] = useState("");

  const [isPublished, setIsPublished] = useState(false);
  const [publishDate, setPublishDate] = useState("");

  const [datesTarget, setDatesTarget] = useState<SalesReviewQueueItem | null>(null);
  const [confirmDeliveryDate, setConfirmDeliveryDate] = useState("");
  const [confirmPlantingDate, setConfirmPlantingDate] = useState("");
  const [pushBackTarget, setPushBackTarget] = useState<SalesReviewQueueItem | null>(null);
  const [pushBackComment, setPushBackComment] = useState("");

  const queue = useMemo(() => queueQuery.data?.items ?? [], [queueQuery.data?.items]);

  const openViewDialog = async (orderId: string) => {
    setViewLoadingId(orderId);
    try {
      setViewTarget(await ordersApi.getOrderById(orderId));
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Could not load order details.");
    } finally {
      setViewLoadingId(null);
    }
  };

  const openReviewDialog = (order: SalesReviewQueueItem) => {
    setReviewTarget(order);
    setDecision(1);
    setReviewNotes("");
  };

  const closeReviewDialog = () => {
    setReviewTarget(null);
    setDecision(1);
    setReviewNotes("");
  };

  const openPublishDialog = (order: SalesReviewQueueItem) => {
    setPublishTarget(order);
    setIsPublished(Boolean(order.isReadyDatePublished));
    setPublishDate(toDateInputValue(order.publishedReadyDate));
  };

  const closePublishDialog = () => {
    setPublishTarget(null);
    setIsPublished(false);
    setPublishDate("");
  };

  const openDatesDialog = (order: SalesReviewQueueItem) => {
    setDatesTarget(order);
    setConfirmDeliveryDate(toDateInputValue(order.confirmedDeliveryDate ?? order.expectedDeliveryDate));
    setConfirmPlantingDate(toDateInputValue(order.confirmedPlantingDate));
  };

  const closeDatesDialog = () => {
    setDatesTarget(null);
    setConfirmDeliveryDate("");
    setConfirmPlantingDate("");
  };

  const openPushBackDialog = (order: SalesReviewQueueItem) => {
    setPushBackTarget(order);
    setPushBackComment("");
  };

  const closePushBackDialog = () => {
    setPushBackTarget(null);
    setPushBackComment("");
  };

  const submitDates = async () => {
    if (!datesTarget) return;
    if (!confirmDeliveryDate && !confirmPlantingDate) {
      toast.error("Set at least one date to confirm.");
      return;
    }
    try {
      await datesMutation.mutateAsync({
        orderId: datesTarget.id,
        payload: {
          confirmedDeliveryDate: confirmDeliveryDate ? new Date(`${confirmDeliveryDate}T00:00:00.000Z`).toISOString() : null,
          confirmedPlantingDate: confirmPlantingDate ? new Date(`${confirmPlantingDate}T00:00:00.000Z`).toISOString() : null,
        },
      });
      toast.success("Dates confirmed. Farmer has been notified.");
      closeDatesDialog();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Could not confirm dates.");
    }
  };

  const submitReview = async () => {
    if (!reviewTarget) return;

    try {
      await confirmMutation.mutateAsync({
        orderId: reviewTarget.id,
        payload: {
          decision,
          notes: reviewNotes.trim() || undefined,
        },
      });
      toast.success(decision === 1 ? "Order confirmed." : "Order deferred.");
      closeReviewDialog();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Could not complete sales review action.");
    }
  };

  const submitPushBack = async () => {
    if (!pushBackTarget) return;
    if (!pushBackComment.trim()) {
      toast.error("Provide amendment guidance before pushing back.");
      return;
    }

    try {
      await pushBackMutation.mutateAsync({
        orderId: pushBackTarget.id,
        comment: pushBackComment.trim(),
      });
      toast.success("Order pushed back to agronomist for amendment.");
      closePushBackDialog();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Could not push back order.");
    }
  };

  const submitPublishReadyDate = async () => {
    if (!publishTarget) return;

    if (isPublished && !publishDate) {
      toast.error("Pick a ready date before publishing.");
      return;
    }

    try {
      await publishMutation.mutateAsync({
        orderId: publishTarget.id,
        payload: {
          isPublished,
          publishedReadyDate: isPublished ? new Date(`${publishDate}T00:00:00.000Z`).toISOString() : null,
        },
      });
      toast.success(isPublished ? "Ready date published." : "Ready date hidden from farmer.");
      closePublishDialog();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Could not update ready date publishing.");
    }
  };

  const visibleBalance = queue.reduce((sum, order) => sum + getBalanceAmount(order), 0);

  if (queueQuery.isLoading) {
    return <div className="p-6 text-sm text-muted-foreground">Loading sales-review queue...</div>;
  }

  if (queueQuery.error) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-4">
        <p className="text-red-600">Failed to load orders awaiting sales review.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Sales Review Queue</h1>
          <p className="mt-1 text-muted-foreground">
            Review-controlled orders awaiting sales confirmation.
          </p>
        </div>
        <Button
          variant="outline"
          onClick={() => {
            void queueQuery.refetch();
          }}
        >
          Refresh
        </Button>
      </div>

      <div className="grid gap-3 sm:grid-cols-3">
        <div className="rounded-lg border bg-background p-4">
          <p className="text-xs uppercase tracking-wide text-muted-foreground">Orders Awaiting Review</p>
          <p className="mt-1 text-2xl font-semibold">{queueQuery.data?.totalCount ?? 0}</p>
        </div>
        <div className="rounded-lg border bg-background p-4">
          <p className="text-xs uppercase tracking-wide text-muted-foreground">Visible Balance</p>
          <p className="mt-1 text-2xl font-semibold">{formatKes(visibleBalance)}</p>
        </div>
        <div className="rounded-lg border bg-background p-4">
          <p className="text-xs uppercase tracking-wide text-muted-foreground">Actions Enabled</p>
          <p className="mt-1 text-sm text-muted-foreground">
            {canConfirm ? "Confirm/Defer" : "No confirm permission"}
            {" • "}
            {canPublishReadyDate ? "Publish date" : "No publish permission"}
            {" • "}
            {canPushBack ? "Push back" : "No push-back permission"}
          </p>
        </div>
      </div>

      <div className="flex items-center gap-3">
        <Input
          placeholder="Search by order, customer, or crop/variety"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          className="max-w-md"
        />
        <Button
          variant="ghost"
          onClick={() => setSearch("")}
          disabled={!search}
        >
          Clear
        </Button>
      </div>

      {queue.length === 0 ? (
        <div className="rounded-lg border bg-muted/40 p-8 text-center text-muted-foreground">
          No orders currently awaiting sales review.
        </div>
      ) : (
        <div className="rounded-lg border overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Order</TableHead>
                <TableHead>Customer</TableHead>
                <TableHead>Items</TableHead>
                <TableHead>Payment</TableHead>
                <TableHead>Published Ready Date</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {queue.map((order) => {
                const balance = getBalanceAmount(order);
                const total = Number(order.totalAmount ?? 0);
                const paid = Number(order.paidAmount ?? 0);
                const paidPct = total > 0 ? Math.round((paid / total) * 100) : 0;

                return (
                  <TableRow key={order.id}>
                    <TableCell>
                      <div>
                        <p className="font-mono text-xs">{order.orderNumber}</p>
                        <p className="text-xs text-muted-foreground">Created {formatDateOrDash(order.createdAt)}</p>
                      </div>
                    </TableCell>
                    <TableCell>{order.customerName}</TableCell>
                    <TableCell className="max-w-[280px]">
                      <p className="truncate" title={getItemSummary(order)}>{getItemSummary(order)}</p>
                    </TableCell>
                    <TableCell>
                      <div className="space-y-0.5 text-sm">
                        <p>{paidPct}% paid</p>
                        <p className="text-xs text-muted-foreground">
                          {formatKes(paid)} / {formatKes(total)}
                        </p>
                        <p className="text-xs text-muted-foreground">Balance: {formatKes(balance)}</p>
                      </div>
                    </TableCell>
                    <TableCell>
                      {order.isReadyDatePublished ? (
                        <Badge variant="default">{formatDateOrDash(order.publishedReadyDate)}</Badge>
                      ) : (
                        <Badge variant="outline">Hidden</Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="space-y-1">
                        <Badge variant="secondary">{order.status}</Badge>
                        {order.isAgronomistSubmission ? (
                          <p className="text-xs text-muted-foreground">
                            Agronomist: {order.submittedByUserName || "Field submission"}
                          </p>
                        ) : null}
                        {order.agronomistAmendmentStatus && order.agronomistAmendmentStatus !== "None" ? (
                          <p className="text-xs text-amber-700">
                            Amendment: {order.agronomistAmendmentStatus}
                          </p>
                        ) : null}
                        {order.reservationDecision ? (
                          <p className="text-xs text-muted-foreground">Decision: {order.reservationDecision}</p>
                        ) : null}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="inline-flex flex-wrap justify-end gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => void openViewDialog(order.id)}
                          disabled={viewLoadingId === order.id}
                        >
                          {viewLoadingId === order.id ? "Loading..." : "View"}
                        </Button>
                        {canConfirm ? (
                          <Button size="sm" onClick={() => openReviewDialog(order)}>
                            Review
                          </Button>
                        ) : null}
                        {canPublishReadyDate ? (
                          <Button size="sm" variant="secondary" onClick={() => openPublishDialog(order)}>
                            Ready Date
                          </Button>
                        ) : null}
                        {canConfirmDates ? (
                          <Button size="sm" variant="secondary" onClick={() => openDatesDialog(order)}>
                            Dates
                          </Button>
                        ) : null}
                        {canPushBack && order.isAgronomistSubmission ? (
                          <Button size="sm" variant="destructive" onClick={() => openPushBackDialog(order)}>
                            Push Back
                          </Button>
                        ) : null}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          <div className="border-t p-4">
            <TablePagination
              pageNumber={queueQuery.data?.pageNumber ?? pageNumber}
              pageSize={queueQuery.data?.pageSize ?? pageSize}
              totalCount={queueQuery.data?.totalCount ?? 0}
              totalPages={Math.ceil((queueQuery.data?.totalCount ?? 0) / pageSize)}
              onPageChange={setPageNumber}
              onPageSizeChange={(size) => {
                setPageSize(size);
                setPageNumber(1);
              }}
              pageSizeOptions={[20, 50, 100]}
              isFetching={queueQuery.isFetching}
            />
          </div>
        </div>
      )}

      <Dialog open={Boolean(reviewTarget)} onOpenChange={(open) => !open && closeReviewDialog()}>
        <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Confirm With Allocation</DialogTitle>
            <DialogDescription>
              {reviewTarget
                ? `Order ${reviewTarget.orderNumber} - ${reviewTarget.customerName}`
                : "Choose reservation decision"}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="rounded-lg border bg-muted/20 p-3">
              {allocationPreviewQuery.isLoading ? (
                <p className="text-sm text-muted-foreground">Calculating stage-priority allocation preview...</p>
              ) : allocationPreviewQuery.error ? (
                <p className="text-sm text-red-600">Could not load allocation preview for this order.</p>
              ) : allocationPreviewQuery.data ? (
                <div className="space-y-3">
                  <div className="grid gap-2 sm:grid-cols-4">
                    <div>
                      <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Requested</p>
                      <p className="text-sm font-semibold">{formatQty(allocationPreviewQuery.data.totalRequestedQuantity)}</p>
                    </div>
                    <div>
                      <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Allocatable</p>
                      <p className="text-sm font-semibold">{formatQty(allocationPreviewQuery.data.totalAllocatedQuantity)}</p>
                    </div>
                    <div>
                      <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Shortfall</p>
                      <p className="text-sm font-semibold text-amber-600">{formatQty(allocationPreviewQuery.data.totalShortfallQuantity)}</p>
                    </div>
                    <div>
                      <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Latest Ready Estimate</p>
                      <p className="text-sm font-semibold">
                        {allocationPreviewQuery.data.isLatestReadyEstimateTbd
                          ? "TBD"
                          : formatDateOrTbd(allocationPreviewQuery.data.latestReadyEstimate)}
                      </p>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                      Stage-Priority Allocations
                    </p>
                    <div className="rounded-md border overflow-hidden">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Stage</TableHead>
                            <TableHead>Available</TableHead>
                            <TableHead>Allocated</TableHead>
                            <TableHead>Latest Stage Estimate</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {allocationPreviewQuery.data.stageAllocations.map((stage) => (
                            <TableRow key={stage.stage}>
                              <TableCell>{stageLabelMap[stage.stage] ?? stage.stage}</TableCell>
                              <TableCell>{formatQty(stage.availableQuantity)}</TableCell>
                              <TableCell>{formatQty(stage.allocatedQuantity)}</TableCell>
                              <TableCell>{formatDateOrTbd(stage.stageReadyEstimate)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </div>

                  <div className="space-y-1">
                    {allocationPreviewQuery.data.items.map((item) => (
                      <p key={item.orderItemId} className="text-xs text-muted-foreground">
                        {item.productLabel}
                        {item.trayTypeLabel ? ` (${item.trayTypeLabel})` : ""}
                        {item.unitLabel ? ` · ${item.unitLabel}` : ""}
                        : {formatQty(item.allocatedQuantity)} / {formatQty(item.requestedQuantity)}
                        {item.shortfallQuantity > 0 ? ` • Shortfall ${formatQty(item.shortfallQuantity)}` : ""}
                      </p>
                    ))}
                  </div>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">No allocation preview available.</p>
              )}
            </div>

            <div className="grid grid-cols-1 gap-2">
              <Button
                type="button"
                variant={decision === 1 ? "default" : "outline"}
                onClick={() => setDecision(1)}
                disabled={!allocationPreviewQuery.data || allocationPreviewQuery.data.totalAllocatedQuantity <= 0}
              >
                Confirm (Reserve Stock)
              </Button>
              <Button
                type="button"
                variant={decision === 3 ? "destructive" : "outline"}
                onClick={() => setDecision(3)}
              >
                Defer
              </Button>
            </div>

            <Textarea
              value={reviewNotes}
              onChange={(event) => setReviewNotes(event.target.value)}
              placeholder="Notes (optional)"
            />
          </div>

          <DialogFooter>
            <Button variant="secondary" onClick={closeReviewDialog}>Cancel</Button>
            <Button onClick={submitReview} disabled={confirmMutation.isPending}>
              {confirmMutation.isPending ? "Submitting..." : decision === 1 ? "Confirm Order" : "Defer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(publishTarget)} onOpenChange={(open) => !open && closePublishDialog()}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Publish Farmer-Visible Ready Date</DialogTitle>
            <DialogDescription>
              {publishTarget
                ? `Order ${publishTarget.orderNumber} - ${publishTarget.customerName}`
                : "Ready date publishing"}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant={isPublished ? "default" : "outline"}
                onClick={() => setIsPublished(true)}
              >
                Publish
              </Button>
              <Button
                type="button"
                variant={!isPublished ? "default" : "outline"}
                onClick={() => setIsPublished(false)}
              >
                Hide
              </Button>
            </div>

            <Input
              type="date"
              value={publishDate}
              onChange={(event) => setPublishDate(event.target.value)}
              disabled={!isPublished}
            />
          </div>

          <DialogFooter>
            <Button variant="secondary" onClick={closePublishDialog}>Cancel</Button>
            <Button onClick={submitPublishReadyDate} disabled={publishMutation.isPending}>
              {publishMutation.isPending ? "Saving..." : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(datesTarget)} onOpenChange={(open) => !open && closeDatesDialog()}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Confirm / Amend Dates</DialogTitle>
            <DialogDescription>
              {datesTarget
                ? `Order ${datesTarget.orderNumber} - ${datesTarget.customerName}`
                : "Date confirmation"}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {datesTarget?.expectedDeliveryDate ? (
              <p className="text-xs text-muted-foreground">
                Farmer requested delivery: {formatDateOrDash(datesTarget.expectedDeliveryDate)}
              </p>
            ) : null}

            <div className="space-y-1">
              <label className="text-sm font-medium">Confirmed Delivery Date</label>
              <DatePicker
                value={confirmDeliveryDate}
                onChange={setConfirmDeliveryDate}
                placeholder="Pick delivery date"
              />
            </div>

            <div className="space-y-1">
              <label className="text-sm font-medium">Confirmed Planting Date</label>
              <DatePicker
                value={confirmPlantingDate}
                onChange={setConfirmPlantingDate}
                placeholder="Pick planting date"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="secondary" onClick={closeDatesDialog}>Cancel</Button>
            <Button onClick={submitDates} disabled={datesMutation.isPending}>
              {datesMutation.isPending ? "Saving..." : "Confirm Dates"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(pushBackTarget)} onOpenChange={(open) => !open && closePushBackDialog()}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Push Back For Amendment</DialogTitle>
            <DialogDescription>
              {pushBackTarget
                ? `Order ${pushBackTarget.orderNumber} - ${pushBackTarget.customerName}`
                : "Provide amendment guidance for agronomist resubmission."}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <Textarea
              value={pushBackComment}
              onChange={(event) => setPushBackComment(event.target.value)}
              placeholder="Explain what must be amended before approval."
              rows={5}
            />
          </div>

          <DialogFooter>
            <Button variant="secondary" onClick={closePushBackDialog}>Cancel</Button>
            <Button variant="destructive" onClick={submitPushBack} disabled={pushBackMutation.isPending}>
              {pushBackMutation.isPending ? "Submitting..." : "Push Back"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {viewTarget ? (
        <ViewOrderDialog
          order={viewTarget}
          open={Boolean(viewTarget)}
          onOpenChange={(open) => {
            if (!open) setViewTarget(null);
          }}
        />
      ) : null}
    </div>
  );
};

export default SalesReviewQueuePage;
